import React from "react";
import {
  Box,
  Flex,
  Grid,
  Heading,
  Icon,
  Link,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useColorModeValue } from "../components/ui/color-mode";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useRecentStocks } from "../hooks/useRecentStocks";
import { useStockInfo } from "../hooks/useStockInfo";
import StockSearch from "../components/StockSearch";
import LoginButton from "../components/LoginButton";
import { FiBarChart2, FiBookOpen, FiFileText, FiRefreshCw, FiTrendingUp } from "react-icons/fi";
import { SIGNATURE_GREEN } from "../theme/brandColors";

const exampleStocks = [
  { ticker: "AAPL", name: "Apple", href: "/stock/aapl" },
  { ticker: "MSFT", name: "Microsoft", href: "/stock/msft" },
  { ticker: "NVDA", name: "NVIDIA", href: "/stock/nvda" },
  { ticker: "BRK-B", name: "Berkshire Hathaway", href: "/stock/brk-b" },
];

const publicMetricLinks = [
  { label: "All public KPI data", href: "/metrics" },
  { label: "User and customer metrics", href: "/metrics/users" },
];

const featureSections = [
  {
    title: "Reverse DCF",
    text: "See the market-implied growth rate and market-implied assumptions embedded in a stock price before deciding whether they are realistic.",
    icon: FiRefreshCw,
  },
  {
    title: "Traditional DCF Calculator",
    text: "Build a discounted cash flow model from revenue, margins, reinvestment, discount rates, terminal value, and intrinsic value per share.",
    icon: FiBarChart2,
  },
  {
    title: "Historical Financials",
    text: "Review income statement, balance sheet, and cash flow history before turning business performance into stock valuation assumptions.",
    icon: FiBookOpen,
  },
  {
    title: "Sensitivity and Charts",
    text: "Compare how valuation changes when growth, margins, discount rates, and terminal assumptions move.",
    icon: FiTrendingUp,
  },
  {
    title: "Filing-Sourced Metrics",
    text: "Use public metrics sourced from company filings to connect SEC disclosures with DCF drivers and operating trends.",
    icon: FiFileText,
  },
];

const howItWorksSteps = [
  "Search for a public company by ticker or company name.",
  "Review financial statements, SEC filings, charts, and filing-sourced metrics.",
  "Use reverse DCF and traditional DCF tools to compare intrinsic value with the current market narrative.",
];


// ---------------- Recent Stock Item Component -----------------
const RecentStockItem = ({ symbol, lastVisitedAt }) => {
  const { data: stockInfo, loading } = useStockInfo(symbol);
  const companyName = stockInfo?.company_name || symbol;
  const navigate = useNavigate();
  
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <Box
      as="button"
      onClick={() => navigate(`/stock/${symbol.toLowerCase()}`)}
      bg="rgba(255, 255, 255, 0.5)"
      _dark={{ bg: "rgba(0, 0, 0, 0.3)" }}
      borderWidth="1px"
      borderRadius="lg"
      p={4}
      textAlign="center"
      w="100%"
      maxW="300px"
      _hover={{ 
        bg: "rgba(255, 255, 255, 0.7)",
        _dark: { bg: "rgba(0, 0, 0, 0.5)" },
        transform: "translateY(-2px)",
        boxShadow: "md"
      }}
      transition="all 0.2s"
      cursor="pointer"
    >
      <VStack spacing={2}>
        <Text fontSize="lg" fontWeight="bold">
          {symbol}
        </Text>
        <Text fontSize="sm" color="gray.600" _dark={{ color: "gray.300" }}>
          {loading ? 'Loading...' : companyName}
        </Text>
        <Text fontSize="xs" color="gray.500" _dark={{ color: "gray.400" }}>
          Last opened: {formatDate(lastVisitedAt)}
        </Text>
      </VStack>
    </Box>
  );
};

// ---------------- Browse page -------------
const Home = () => {
  const { isAuthenticated } = useAuth0();
  const { recents, loading: recentsLoading, error: recentsError, addRecent } = useRecentStocks();

  const handleSuggestionSelected = async (suggestion) => {
    try {
      await addRecent?.(suggestion.ticker);
    } catch (err) {
      console.error("Failed to record recent stock", err);
    }
  };

  const pageBg = useColorModeValue("gray.50", "gray.950");
  const bandBg = useColorModeValue("white", "gray.900");
  const subtleBg = useColorModeValue("gray.100", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const textColor = useColorModeValue("gray.800", "white");
  const mutedTextColor = useColorModeValue("gray.600", "gray.300");
  const linkColor = useColorModeValue("teal.700", "teal.300");
  const linkHoverBg = useColorModeValue("teal.50", "whiteAlpha.100");
  const heroLinkProps = {
    color: linkColor,
    fontWeight: "semibold",
    textDecoration: "underline",
    textDecorationStyle: "dotted",
    textUnderlineOffset: "4px",
    transition: "background-color 0.2s ease, color 0.2s ease, text-decoration-color 0.2s ease",
    px: 1,
    borderRadius: "sm",
    _hover: {
      bg: linkHoverBg,
      color: SIGNATURE_GREEN,
      textDecoration: "underline",
      textDecorationStyle: "solid",
    },
  };

  // ---------------- JSX -------------
  return (
    <>
      <Box bg={pageBg} color={textColor}>
        <Box as="section" bg={bandBg} borderBottomWidth="1px" borderColor={borderColor}>
          <Grid
            maxW="1180px"
            mx="auto"
            px={{ base: 5, md: 8 }}
            py={{ base: 12, md: 16 }}
            gap={{ base: 10, lg: 14 }}
            templateColumns={{ base: "1fr", lg: "minmax(0, 1.1fr) minmax(340px, 0.9fr)" }}
            alignItems="center"
          >
            <VStack align="start" gap={5}>
              <Text
                w="100%"
                textAlign="center"
                fontSize={{ base: "2xl", md: "3xl" }}
                fontWeight="bold"
                color={SIGNATURE_GREEN}
                style={{ color: SIGNATURE_GREEN }}
                textTransform="uppercase"
              >
                DCF Teacher
              </Text>
              <Heading as="h1" size={{ base: "3xl", md: "4xl" }} lineHeight="1.05">
                Learn stock valuation with discounted cash flow tools.
              </Heading>
              <Text fontSize={{ base: "lg", md: "xl" }} color={mutedTextColor} lineHeight="1.7">
                DCF Teacher combines a DCF calculator, reverse DCF analysis, historical financials,
                SEC filings, charts, and filing-sourced public metrics so investors can connect a
                company&apos;s fundamentals to intrinsic value.
              </Text>
              <LoginButton
                size="lg"
                alignSelf="center"
                bg={SIGNATURE_GREEN}
                color="white"
                style={{ backgroundColor: SIGNATURE_GREEN }}
                transition="transform 0.2s ease, box-shadow 0.2s ease, filter 0.2s ease"
                _hover={{
                  bg: SIGNATURE_GREEN,
                  boxShadow: "lg",
                  filter: "brightness(0.95)",
                  transform: "translateY(-2px)",
                }}
              >
                Create a Free Account
              </LoginButton>
              <Flex wrap="wrap" gap={3} pt={2}>
                <Link href="/browse" {...heroLinkProps}>
                  Browse stocks
                </Link>
                <Link href="/metrics" {...heroLinkProps}>
                  Public KPI data
                </Link>
                <Link href="/tutorials" {...heroLinkProps}>
                  Learn DCF
                </Link>
                <Link href="/stock-baskets" {...heroLinkProps}>
                  Explore stock baskets
                </Link>
              </Flex>
            </VStack>

            <Box
              bg={subtleBg}
              borderWidth="1px"
              borderColor={borderColor}
              borderRadius="md"
              p={{ base: 5, md: 6 }}
            >
              <VStack align="stretch" gap={4}>
                <Box>
                  <Heading as="h2" size="md">
                    Search stocks
                  </Heading>
                  <Text mt={2} color={mutedTextColor}>
                    Start with a ticker to open valuation tools, SEC filings, financial statements,
                    reverse DCF, and chart views.
                  </Text>
                </Box>
                <Box>
                  <Text
                    as="label"
                    htmlFor="homepage-stock-search"
                    display="block"
                    fontSize="sm"
                    fontWeight="semibold"
                    mb={2}
                  >
                    Stock search
                  </Text>
                  <StockSearch
                    onSuggestionSelected={handleSuggestionSelected}
                    inputProps={{
                      id: "homepage-stock-search",
                      "aria-label": "Search by ticker or company name",
                    }}
                  />
                </Box>
                <Box>
                  <Text fontSize="sm" fontWeight="semibold" mb={2}>
                    Example stocks
                  </Text>
                  <Flex wrap="wrap" gap={2}>
                    {exampleStocks.map((stock) => (
                      <Link
                        key={stock.ticker}
                        href={stock.href}
                        px={3}
                        py={2}
                        bg={bandBg}
                        borderWidth="1px"
                        borderColor={borderColor}
                        borderRadius="md"
                        color={textColor}
                        fontWeight="semibold"
                      >
                        {stock.ticker} <Text as="span" color={mutedTextColor} fontWeight="normal">{stock.name}</Text>
                      </Link>
                    ))}
                  </Flex>
                </Box>
              </VStack>
            </Box>
          </Grid>
        </Box>

        <Box as="section" maxW="1180px" mx="auto" px={{ base: 5, md: 8 }} py={{ base: 10, md: 14 }}>
          <VStack align="start" gap={4} mb={7}>
            <Heading as="h2" size="xl">
              Valuation tools for the full DCF workflow
            </Heading>
            <Text color={mutedTextColor} maxW="760px" lineHeight="1.7">
              Move from raw company data to a defensible stock valuation model without losing the
              connection between business performance, market-implied assumptions, and intrinsic value.
            </Text>
          </VStack>
          <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} gap={5}>
            {featureSections.map((feature) => (
              <Box
                key={feature.title}
                bg={bandBg}
                borderWidth="1px"
                borderColor={borderColor}
                borderRadius="md"
                p={5}
              >
                <Flex align="center" gap={2} mb={3}>
                  {feature.icon && (
                    <Icon as={feature.icon} boxSize={5} color={linkColor} aria-hidden="true" />
                  )}
                  <Heading as="h3" size="md">
                    {feature.title}
                  </Heading>
                </Flex>
                <Text color={mutedTextColor} lineHeight="1.65">
                  {feature.text}
                </Text>
              </Box>
            ))}
          </SimpleGrid>
        </Box>

        <Box as="section" bg={bandBg} borderYWidth="1px" borderColor={borderColor}>
          <Box maxW="1180px" mx="auto" px={{ base: 5, md: 8 }} py={{ base: 10, md: 14 }}>
            <Grid templateColumns={{ base: "1fr", md: "0.9fr 1.1fr" }} gap={8} alignItems="start">
              <Box>
                <Heading as="h2" size="xl">
                  Filing-sourced public KPI data
                </Heading>
                <Text mt={3} color={mutedTextColor} lineHeight="1.7">
                  Public metric pages collect company-reported operating data, source filing links,
                  and historical KPI values that can feed stock valuation assumptions.
                </Text>
              </Box>
              <SimpleGrid columns={{ base: 1, sm: 2 }} gap={3}>
                {publicMetricLinks.map((metricLink) => (
                  <Link
                    key={metricLink.href}
                    href={metricLink.href}
                    bg={subtleBg}
                    borderWidth="1px"
                    borderColor={borderColor}
                    borderRadius="md"
                    p={4}
                    color={linkColor}
                    fontWeight="semibold"
                  >
                    {metricLink.label}
                  </Link>
                ))}
              </SimpleGrid>
            </Grid>
          </Box>
        </Box>

        <Box as="section" bg={bandBg} borderYWidth="1px" borderColor={borderColor}>
          <Box maxW="1180px" mx="auto" px={{ base: 5, md: 8 }} py={{ base: 10, md: 14 }}>
            <Grid templateColumns={{ base: "1fr", md: "0.8fr 1.2fr" }} gap={8}>
              <Box>
                <Heading as="h2" size="xl">
                  How it works
                </Heading>
                <Text mt={3} color={mutedTextColor} lineHeight="1.7">
                  DCF Teacher is built for investors who want to understand the assumptions behind a
                  valuation instead of stopping at a headline multiple.
                </Text>
              </Box>
              <VStack as="ol" align="stretch" gap={3} m={0} pl={0} listStyleType="none">
                {howItWorksSteps.map((step, index) => (
                  <Flex
                    as="li"
                    key={step}
                    gap={4}
                    bg={subtleBg}
                    borderWidth="1px"
                    borderColor={borderColor}
                    borderRadius="md"
                    p={4}
                  >
                    <Text fontWeight="bold" color={linkColor}>
                      {index + 1}
                    </Text>
                    <Text color={mutedTextColor}>{step}</Text>
                  </Flex>
                ))}
              </VStack>
            </Grid>
          </Box>
        </Box>
        
        {isAuthenticated && (
          <Box 
            bg="rgba(255, 255, 255, 0.5)" 
            _dark={{ bg: "rgba(0, 0, 0, 0.3)" }}
            borderTopWidth="1px" 
            py={6} 
            px={6} 
            mt={8} 
            w="100%"
          >
            <VStack spacing={6} maxW="1200px" mx="auto" align="center">
              <Text fontSize="xl" fontWeight="semibold" textAlign="center">
                Recently visited
              </Text>
              {recentsLoading && <Spinner size="sm" />}
              {recentsError && (
                <Text fontSize="sm" color="red.500" textAlign="center">
                  {recentsError}
                </Text>
              )}
              {!recentsLoading && !recentsError && recents.length === 0 && (
                <Text fontSize="sm" color="gray.500" textAlign="center">
                  Your recent stocks will appear here after you browse.
                </Text>
              )}
              {!recentsLoading && recents.length > 0 && (
                <Flex wrap="wrap" gap={4} justify="center">
                  {recents.map((r) => (
                    <RecentStockItem 
                      key={r.symbol} 
                      symbol={r.symbol} 
                      lastVisitedAt={r.last_visited_at}
                    />
                  ))}
                </Flex>
              )}
            </VStack>
          </Box>
        )}

        <Box as="footer" maxW="1180px" mx="auto" px={{ base: 5, md: 8 }} py={8}>
          <Flex wrap="wrap" gap={4} justify="space-between" align="center">
            <Text fontSize="sm" color={mutedTextColor}>
              DCF Teacher helps investors learn discounted cash flow and reverse DCF analysis.
            </Text>
            <Flex wrap="wrap" gap={4}>
              <Link href="/about" color={linkColor}>About</Link>
              <Link href="/tutorials" color={linkColor}>Tutorials</Link>
              <Link href="/metrics" color={linkColor}>Public KPI Data</Link>
              <Link href="/browse" color={linkColor}>Browse</Link>
              <Link href="/changelog" color={linkColor}>Changelog</Link>
            </Flex>
          </Flex>
        </Box>
      </Box>
    </>
  );
};

export default Home;
